
import * as React from 'react';
import {
  IContextualMenuItem,
  ContextualMenuItemType
} from 'office-ui-fabric-react/lib/ContextualMenu';
import { mapObject, makeObject, assign } from '../../utilities/objects';
import { ICommand, ICommandStateProps } from '../../models/commands/ICommand';
import {
  WithActionSet,
  IWithActionSetStateProps,
  INITIAL_ACTION_STATE,
  IOnExecuteProps,
  evaluateAction
} from '../withAction/WithAction';
import { IComponentDecorator, IDecoratedComponentType, IDecoratedProps } from '../../utilities/components/Decorators';
import { IRenderProps, renderChildrenWithProps, StatePropsUpdater } from '../../utilities/components/RenderProps';
import { mark } from '../../utilities/performance';
import { resetPerfLoggerFacet, resetPerfLoggerKeyFacet } from '../../facets/LoggerFacets';
import { readItem } from '../../operations/readItem';
import { getTelemetry } from '../telemetry/TelemetryProvider';
import { Engagement } from '@ms/telemetry-manager/lib/events/Engagement.event';
import { IItemCache } from '../../models/IItemCacheObserver';
import { BackgroundTaskContextProvider } from '../backgroundTaskRunner/BackgroundTaskHost';
import { WithItemCache, IItemCacheStateProps } from '../withItemCache/WithItemCache';

export { IOnExecuteProps };

const NO_COMMANDS: ReadonlyArray<ICommand> = [];

/**
 * @public
 */
export interface ICommandSetStateProps {
  /**
   * An array of contextual menu items to bind to a Fabric command component.
   */
  menuItems: ReadonlyArray<IContextualMenuItem & ICommandStateProps>;
}

/**
 * @public
 */
export interface ICommandSetProps extends IRenderProps<ICommandSetStateProps> {
  /**
   * An array of commands to filter for avilability and to which to bind click handlers.
   */
  commands: ReadonlyArray<ICommand> | undefined;
  onMenuItemsChanged?: (menuItems: ICommandSetStateProps['menuItems']) => void;
}

/**
 * Component which ingests a set of `ICommand` instances, `ICommandBarItemProps` instances, or `IContextualMenuItem` instances
 * and filters them to what is available, accounting for any `action` or `postProcess` hooks in the commands.
 * @public
 */
export class CommandSet extends React.PureComponent<ICommandSetProps> {
  public render(): JSX.Element {
    const {
      commands = NO_COMMANDS,
      children,
      onMenuItemsChanged
    } = this.props;

    return (
      <BackgroundTaskContextProvider scope='local'>
        <WithItemCache>
          { ({ itemCache, dispatch }: IItemCacheStateProps) => {
            const { telemetry } = getTelemetry(itemCache);

            const actions = extractCommandActionElements(itemCache, commands, '');

            function logCommandStart(command: ICommand): void {
              const resetPerfLoggerKey = resetPerfLoggerKeyFacet.serialize({});

              const { resetPerfLogger } = dispatch(readItem({
                resetPerfLogger: resetPerfLoggerFacet
              }, resetPerfLoggerKey));

              const {
                engagement
              } = command;

              if (engagement) {
                telemetry(Engagement).logData({
                  usageType: 'Click',
                  ...engagement
                });
              }

              if (resetPerfLogger) {
                resetPerfLogger();
              }

              mark(`EUPL.${command.automationId}.click`);
            }

            return (
              <WithActionSet actions={ actions }>
                { (state: IWithActionSetStateProps): JSX.Element | null => {
                  const menuItems = filterCommands(commands, state, '', logCommandStart);

                  return (
                    <>
                      {
                        onMenuItemsChanged ? (
                          <StatePropsUpdater stateProps={ menuItems } onUpdate={ onMenuItemsChanged } />
                        ) : null
                      }
                      {
                        renderChildrenWithProps<ICommandSetStateProps>(children, {
                          menuItems
                        })
                      }
                    </>
                  );
                } }
              </WithActionSet>
            );
          } }
        </WithItemCache>
      </BackgroundTaskContextProvider>
    );
  }
}

/**
 * Filters a tree of contextual menu commands using current action state and
 * produces final versions of the commands with unavailable commands removed,
 * onClick callbacks wired up, and sub-menus filtered.
 */
function filterCommands(
  commands: ReadonlyArray<ICommand>,
  state: IWithActionSetStateProps,
  baseKey: string,
  logCommandStart?: (command: ICommand) => void
): ReadonlyArray<IContextualMenuItem & ICommandStateProps> {
  const items: (IContextualMenuItem & ICommandStateProps)[] = [];

  for (const command of commands) {
    const {
      action,
      automationId,
      postProcess,
      subMenuProps,
      ...menuItemProps
    } = command;

    const menuItem: IContextualMenuItem = {
      ...menuItemProps,
      ...(automationId ? { 'data-automationid': automationId } : {})
    };

    const key = `${baseKey}-${command.key}`;

    const actionState = state.actions[key] || INITIAL_ACTION_STATE;

    const isActionVisible = actionState.isAvailable || actionState.isToggled || command.isPermanent;
    const childItems = subMenuProps && (action && isActionVisible || !action) &&
      filterCommands(subMenuProps.items, state, key, logCommandStart);

    let item: IContextualMenuItem | undefined;

    if (action || subMenuProps) {
      if (isActionVisible || childItems && childItems.length > 0) {
        item = {
          ...menuItem,
          ...(isActionVisible ? {
            disabled: !actionState.isAvailable && !actionState.isToggled,
            canCheck: typeof actionState.isToggled === 'boolean',
            checked: actionState.isToggled,
            ...(actionState.isAvailable ? {
              onClick: (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                if (logCommandStart) {
                  logCommandStart(command);
                }

                // TODO Add QoS logging.
                if (ev) {
                  // Synthetic event will be reused for perf reasons unless
                  // we use event.persist(). https://fb.me/react-event-pooling
                  ev.persist();
                }

                if (command.onClick) {
                  command.onClick(ev);
                }

                actionState.execute({ event: ev }).catch((error: Error) => {
                  // Nowhere to put the error, so ignore it.
                });
              }
            } : {}),
            actionState: actionState
          } : {}),
          ...(childItems && childItems.length > 0 ? {
            ...(subMenuProps ? {
              subMenuProps: {
                ...subMenuProps,
                isBeakVisible: false,
                items: childItems as IContextualMenuItem[]
              },
              onMenuClick: (ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                if (logCommandStart) {
                  logCommandStart(command);
                }

                if (command.onMenuClick) {
                  command.onMenuClick(ev);
                }
              }
            } : {})
          } : {})
        };
      }
    } else {
      item = menuItem;
    }

    if (item) {
      items.push(postProcess ? postProcess(item) : item);
    }
  }

  return cleanUpDividers(items);
}

/**
 * Processes an array of contextual menu items to ensure that only single dividers are between
 * other items.
 */
function cleanUpDividers(items: ReadonlyArray<IContextualMenuItem>): ReadonlyArray<IContextualMenuItem> {
  let isDivider = true;

  const finalItems: IContextualMenuItem[] = [];

  for (const item of items) {
    if (item.itemType === ContextualMenuItemType.Divider) {
      if (isDivider) {
        continue;
      } else {
        isDivider = true;
      }
    } else {
      isDivider = false;
    }

    finalItems.push(item);
  }

  if (isDivider) {
    finalItems.pop();
  }

  return finalItems;
}

const NO_ITEMS: ReadonlyArray<ICommand> = [];

/**
 * Connects a component to zero or more 'command sets', which are groups of commands that evaluate into
 * menu items.
 * @internal
 */
export function connectToCommandSets<TInjectedProps, TOwnProps, K extends string>(
  mapPropsToCommands: (props: TOwnProps) => { [P in K]: ReadonlyArray<ICommand> | undefined; },
  mapMenuItemsToProps: (
    menuItems: { [P in K]: ReadonlyArray<IContextualMenuItem & ICommandStateProps> },
    props: TOwnProps
  ) => TInjectedProps
): IComponentDecorator<TInjectedProps, TOwnProps> {
  return <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ): IDecoratedComponentType<P, TInjectedProps, TOwnProps> => {
    const Component = component;

    type IProps = IDecoratedProps<P, TInjectedProps, TOwnProps>;

    class CommandSetConnector extends React.PureComponent<IProps> {
      public render(): JSX.Element {
        const mappedCommands = mapPropsToCommands(this.props as Readonly<TOwnProps>);

        const rootCommands: ICommand[] = [];

        for (const key in mappedCommands) {
          if (Object.prototype.hasOwnProperty.call(mappedCommands, key)) {
            const commands = mappedCommands[key];

            rootCommands.push({
              key: key,
              subMenuProps: {
                items: commands as ICommand[] || NO_ITEMS
              }
            });
          }
        }

        return (
          <CommandSet
            commands={ rootCommands }
          >
            { ({ menuItems }: ICommandSetStateProps) => {
              const mappedMenuItems = {
                ...mapObject(mappedCommands as { [R in K]: {} }, () => NO_ITEMS) as {},
                ...makeObject(
                  menuItems as IContextualMenuItem[],
                  (rootCommand: IContextualMenuItem) => {
                    return rootCommand && rootCommand.subMenuProps && rootCommand.subMenuProps.items || NO_ITEMS;
                  },
                  (rootCommand: IContextualMenuItem) => rootCommand.key as K) as {}
              } as { [R in K]: ReadonlyArray<IContextualMenuItem & ICommandStateProps>; };

              const props: P = {
                ...(this.props as {}),
                ...(mapMenuItemsToProps(mappedMenuItems, this.props as Readonly<TOwnProps>) as {})
              } as P;

              return (
                <Component { ...props } />
              );
            } }
          </CommandSet>
        );
      }
    }

    return CommandSetConnector;
  };
}

/**
 * Extracts a mapping of action elements from an input tree of commands,
 * eagerly-evaluating their state from the Item Cache, if possible.
 */
function extractCommandActionElements(
  itemCache: IItemCache,
  currentCommands: ReadonlyArray<ICommand> | undefined,
  baseKey: string
): {
  [key: string]: JSX.Element | null;
} {
  const actions: {
    [key: string]: JSX.Element | null;
  } = {};

  if (currentCommands) {
    for (const command of currentCommands) {
      const {
        action
      } = command;

      const key = `${baseKey}-${command.key}`;

      if (action) {
        actions[key] = evaluateAction(itemCache, action);
      }

      assign(actions, extractCommandActionElements(itemCache, command.subMenuProps && command.subMenuProps.items, key));
    }
  }

  return actions;
}
